import { defineStore } from 'pinia'
import { ref } from 'vue'
import { expectArray } from '../composables/expectShape'
import { useThreadSubmitter } from '../composables/useThreadSubmitter'

export interface Citation {
  path: string
}

export interface ThreadMessage {
  role: 'user' | 'assistant'
  content: string
  citations?: Citation[]
  // True when the answer cited nothing or only stub pages — the
  // drawer renders an "add a source" nudge under such a turn.
  weak_citation?: boolean
  created_at?: string
}

export interface ThreadSummary {
  id: number
  title: string
  last_message_at: string | null
  created_at: string
  turn_count: number
}

export interface ThreadState {
  id: number
  title: string
  messages: ThreadMessage[]
  last_message_at: string | null
  created_at: string
}

/**
 * Manages the multi-turn Query flow. Each thread lives on the Rails side
 * as a Conversation row — the store holds an in-memory projection of the
 * currently-open thread plus a "recent threads" list for the drawer.
 *
 * Submit hits `POST /api/v1/queries/stream` first for token-
 * by-token streaming (sub-1s TTFT). If the stream connection fails BEFORE
 * any delta arrives, falls back to `POST /api/v1/queries`. Mid-stream
 * failures don't fall back — the assistant bubble already has partial
 * content the user can see. Cancel via `AbortController.abort()` works
 * for both paths (fetch's signal propagation closes the ReadableStream).
 *
 * The two transports plus error-rollback live in `useThreadSubmitter` —
 * `open` / `loadRecent` / `reset` / `cancel` stay store-resident state.
 */
export const useThreadsStore = defineStore('threads', () => {
  const current = ref<ThreadState | null>(null)
  const recent = ref<ThreadSummary[]>([])
  const submitting = ref(false)
  const submitError = ref<string | null>(null)
  // Held outside the reactive surface — only used to abort the in-flight
  // fetch and clear its own slot when settled, never read by templates.
  let activeController: AbortController | null = null

  const { trySubmitStreaming, submitSync, rollbackOnError } = useThreadSubmitter(current, submitError)

  async function submit(question: string): Promise<void> {
    submitting.value = true
    submitError.value = null
    const controller = new AbortController()
    activeController = controller
    const userTurn: ThreadMessage = {
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    }
    if (current.value) current.value.messages = [...current.value.messages, userTurn]

    try {
      const streamed = await trySubmitStreaming(question, userTurn, controller.signal)
      if (!streamed) {
        // Streaming connection failed before any delta arrived. Fall back to
        // the legacy sync endpoint so the user still gets an answer; existing
        // CLI / eval callers were always on this path so it's well-tested.
        await submitSync(question, userTurn, controller.signal)
      }
    } catch (e) {
      rollbackOnError(userTurn, e)
      if (!(e instanceof DOMException && e.name === 'AbortError')) throw e
    } finally {
      submitting.value = false
      if (activeController === controller) activeController = null
    }
  }

  function cancel(): void {
    activeController?.abort()
  }

  async function loadRecent(): Promise<void> {
    try {
      const res = await fetch('/api/v1/conversations', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) return
      recent.value = expectArray<ThreadSummary>(await res.json(), 'threads (recent)')
    } catch {
      // Silent: best-effort recent load.
    }
  }

  async function open(id: number): Promise<void> {
    try {
      const res = await fetch(`/api/v1/conversations/${id}`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!res.ok) {
        submitError.value = res.status === 404 ? 'Thread not found' : 'Failed to load thread'
        return
      }
      const data = (await res.json()) as ThreadState & { messages: ThreadMessage[] }
      current.value = {
        id: data.id,
        title: data.title,
        messages: expectArray<ThreadMessage>(data.messages ?? [], 'thread'),
        last_message_at: data.last_message_at,
        created_at: data.created_at,
      }
    } catch (e) {
      submitError.value = e instanceof Error ? e.message : 'Failed to load thread'
    }
  }

  function reset(): void {
    current.value = null
    submitError.value = null
  }

  // Uniform Subscribable interface for useAdminSubscriptions. Threads has
  // no ActionCable channel; subscribe() fires the best-effort recent load
  // so the composable can call store.subscribe() uniformly across all stores.
  function subscribe(): void {
    void loadRecent()
  }

  return { current, recent, submitting, submitError, submit, cancel, loadRecent, subscribe, open, reset }
})
